import { ResultsetItem } from '@interfaces/resultset-item';
import { DatasetDetails } from '@interfaces/dataset';

/**
 * Obtain the import date of the dataset related to the resultset and compare the dates to see which one is newer
 * @param resultset The resultset we need to test.
 * @param datasetMap The hashmap of all datasets where we need to obtain the parent from
 * @returns True if date of parent is newer. False otherwise.
 */
export const isDatasetNewer = (resultset: ResultsetItem, datasetMap: Map<number, DatasetDetails>) => {
  // Early return if resultset or Run_Date is undefined/null
  if (!resultset || !resultset.Run_Date) {
    return false;
  }

  const found = datasetMap.get(resultset.DataSetID);
  if (found) {
    try {
      return new Date(found.DataSetImportDate) > new Date(resultset.Run_Date);
    } catch (error) {
      console.error(
        `Error parsing the dates when comparing resultsets with their imported dataset \nOrigin: ${resultset.Run_Date} \nDataset: ${resultset.DataSetName}`
      );
      // Return base case if comparison was not possible
      return false;
    }
  }
  // Return base case if we can't find a matching dataset (i.e. was deleted)
  return false;
};

/**
 * Obtain the parent of a resultset from the list of results and compare the dates to see which one is newer
 * @param resultset The resultset we need to test. Note: we are only interested in resultsets where Is_Stress is true
 * @param resultList The list of all resultsets where we need to obtain the parent from
 * @returns True if date of parent is newer. False otherwise.
 */
export const isBaselineDateNewerThanStress = (resultset: ResultsetItem, resultList: ResultsetItem[]): boolean => {
  // Early return if resultset or Run_Date is undefined/null
  if (!resultset || !resultset.Run_Date) {
    return false;
  }

  // Process only stresses
  if (resultset.Is_Stress) {
    // Find parent
    const found = resultList.find(rs => rs.RunID === resultset.RunID_Used);
    if (found && found.Run_Date) {
      // Try parsing dates and compare them
      try {
        return new Date(found.Run_Date) > new Date(resultset.Run_Date);
      } catch (err) {
        console.error(
          `Error parsing the dates when comparing resultsets to verify if they're out of date \nOrigin: ${resultset.Run_Date} \nParent: ${found.Run_Date}`
        );
        // Return base case if comparison was not possible
        return false;
      }
    }
  }
  // Return base case if we're a Baseline
  return false;
};

/**
 * Checks if dataset id is still present in the collection of datasets
 * @param resultset The resultset containing a DataSetId property to test
 * @param datasetMap The collection of datasets to test against
 */
export const isDatasetStillInDB = (resultset: ResultsetItem, datasetMap: Map<number, DatasetDetails>): boolean => {
  // Early return if resultset is undefined/null
  if (!resultset) {
    return false;
  }
  return datasetMap.has(resultset.DataSetID);
};
